import React, { useCallback, useMemo } from 'react';
import { Tooltip } from 'antd';
import {
  AnchorLayerType,
  CLSTAG,
  EffectDateType,
  EffectVehicleTypeText,
  WeekLimitText,
  getAnchorLayerText,
} from '@/utils/constant';
import { clstagStr } from '../utils';
import { MapStatus } from '../utils/config';
import {
  getNavigationEnumLabel,
  getNavigationDirectionLabels,
  type NavigationEnumOptions,
} from '../utils/navigation';
import { MAP_EDITOR_DETAIL_TOOLTIP_Z_INDEX } from '../utils/zIndex';
import { getEffectiveMapLabels } from '../utils/effectiveMap';

// 图标提升到模块级，避免每次渲染重新 require
const speedIcon = require('@/assets/image/speed_icon.png');
const userIcon = require('@/assets/image/user_icon.png');
const vehicleIcon = require('@/assets/image/vehicle_icon.png');
const timeIcon = require('@/assets/image/time_icon.png');
const remarkIcon = require('@/assets/image/remark_icon.png');
const editTimeIcon = require('@/assets/image/edit_time.png');

type PolygonPopupProps = {
  data: any;
  onDelete: (data: any) => void;
  onEdit: (data: any) => void;
  onChangeStatus: (data: any) => void;
  onClose: () => void;
  getOverlayContainer: () => HTMLElement | null;
  navigationEnums?: NavigationEnumOptions;
};

/**
 * 地图多边形选中后的 Popup 内容面板
 *
 * 职责：
 * 1. 展示选中动态地图的关键信息：类型、操作人、生效车辆/时间、备注、更新时间等
 * 2. 提供启用/停用、删除、编辑三个快捷操作按钮，点击后通过 props 回调给上层处理
 *
 * 说明：
 * - 该组件渲染的内容会被挂载到地图覆盖物弹窗容器中，因此 getOverlayContainer 用于定位 Popover/Tooltip
 * - 原实现使用 document.createElement 手写 DOM，此处改为 React 组件以便维护，
 *   类名与字段保持与原实现一致，样式仍由 index.scss 提供
 */
const PolygonPopup: React.FC<PolygonPopupProps> = ({
  data,
  onDelete,
  onEdit,
  onChangeStatus,
  onClose,
  getOverlayContainer,
  navigationEnums = { topology: [], direction: [] },
}) => {
  const {
    id,
    type,
    subType,
    subTypeName,
    effectDateType,
    timeLimit,
    operator,
    status,
    remark,
    weekLimit,
    vehicleLimit,
    effectVehicleType,
    modifyTime,
    speedLimit,
    topology,
    direction,
    effectiveMapList,
  } = data;

  /** 派生展示数据用 useMemo 缓存，避免父组件轻微重渲染时重复计算 */
  const typeName = useMemo(() => getAnchorLayerText(type, subType), [type, subType]);
  const isSpeedLimit = useMemo(
    () => type === AnchorLayerType.SPEED_LIMIT,
    [type]
  );
  const isNavigation = useMemo(
    () => type === AnchorLayerType.NAVIGATION,
    [type]
  );
  const changeStatusText = useMemo(
    () => (status === MapStatus.RS_INUSE ? '停用' : '启用'),
    [status]
  );
  const changeStatusClstag = useMemo(
    () =>
      status === MapStatus.RS_INUSE
        ? clstagStr(CLSTAG.Disable)
        : clstagStr(CLSTAG.Enable),
    [status]
  );

  const vehicleLimitText = useMemo(() => {
    const base = EffectVehicleTypeText.get(effectVehicleType) ?? '';
    const limit =
      vehicleLimit && vehicleLimit.length > 0
        ? '(' + vehicleLimit.join(',') + ')'
        : '';
    return base + limit;
  }, [effectVehicleType, vehicleLimit]);

  const vehicleTooltipTitle = useMemo(
    () =>
      vehicleLimit && vehicleLimit.length > 0 ? vehicleLimit.join(',') : '',
    [vehicleLimit]
  );

  const effectTimeText = useMemo(() => {
    if (effectDateType !== EffectDateType.SEASONAL) {
      return '长期生效';
    }
    const weekText = weekLimit
      ? weekLimit.map((v: string) => WeekLimitText.get(v)).join('、')
      : '';
    const timeText = timeLimit
      ? weekLimit
        ? ',' + timeLimit
        : timeLimit
      : '';
    return `周期生效(${weekText}${timeText})`;
  }, [effectDateType, timeLimit, weekLimit]);

  /** Modal/Tooltip 挂载容器 */
  const getLocalPopupContainer = useCallback(
    () => getOverlayContainer() || document.body,
    [getOverlayContainer]
  );

  /** 操作回调：透传 data，避免内联箭头函数 */
  const handleChangeStatus = useCallback(
    () => onChangeStatus(data),
    [onChangeStatus, data]
  );
  const handleDelete = useCallback(() => onDelete(data), [onDelete, data]);
  const handleEdit = useCallback(() => onEdit(data), [onEdit, data]);
  const handleClose = useCallback(() => onClose(), [onClose]);

  return (
    <div className="map-editor-pop-wrapper">
      <div className="operate">
        <div className="type">
          {type === AnchorLayerType.ROAD_TEST_FOCUS_AREA && subTypeName
            ? `${typeName}-${subTypeName}`
            : typeName}
        </div>
        <div className="operate-btns">
          <div
            className="btn"
            clstag={`{${changeStatusClstag}}`}
            id={`change-status-btn-${id}`}
            onClick={handleChangeStatus}
          >
            {changeStatusText}
          </div>
          <div
            className="btn"
            clstag={`{${clstagStr(CLSTAG.Delete)}}`}
            id={`delete-btn-${id}`}
            onClick={handleDelete}
          >
            删除
          </div>
          <div
            className="btn edit"
            clstag={`{${clstagStr(CLSTAG.Edit)}}`}
            id={`edit-btn-${id}`}
            onClick={handleEdit}
          >
            编辑
          </div>
        </div>
        <div
          className="close-btn"
          role="button"
          aria-label="关闭详情"
          onClick={handleClose}
        >
          ×
        </div>
      </div>
      <div className="detail">
        {isSpeedLimit ? (
          <div className="field">
            <img className="icon" src={speedIcon} />
            <div>{`${speedLimit}km/h`}</div>
          </div>
        ) : (
          <div className="line-one">
            <div className="field">
              <img className="icon" src={userIcon} />
              <div className="username">{operator ?? ''}</div>
            </div>
            {/* 旧代码用 document.createElement('Tooltip') 造了个无效的原生元素,
                省略号 hover 的提示从未生效;此处改用真正的 antd Tooltip(有意改进) */}
            <Tooltip
              placement="top"
              title={vehicleTooltipTitle}
              getPopupContainer={getLocalPopupContainer}
              overlayClassName="map-editor-detail-tooltip"
              overlayStyle={{ zIndex: MAP_EDITOR_DETAIL_TOOLTIP_Z_INDEX }}
            >
              <div className="field">
                <img className="icon" src={vehicleIcon} />
                <div className="vehicle-limit">{vehicleLimitText}</div>
              </div>
            </Tooltip>
          </div>
        )}

        {isSpeedLimit ? (
          <div className="field">
            <img className="icon" src={userIcon} />
            <div className="username">{operator ?? ''}</div>
          </div>
        ) : (
          <div className="field">
            <img className="icon" src={timeIcon} />
            <div>{effectTimeText}</div>
          </div>
        )}

        {isNavigation && (
          <>
            <div className="field navigation-field">
              <span className="label">拓扑关系：</span>
              <span>{getNavigationEnumLabel(navigationEnums.topology, topology)}</span>
            </div>
            <div className="field navigation-field">
              <span className="label">行驶方向：</span>
              <span>{getNavigationDirectionLabels(navigationEnums.direction, direction)}</span>
            </div>
          </>
        )}
        {type === AnchorLayerType.ROAD_CLOSED &&
          effectiveMapList?.length > 0 && (
          <div className="field effective-map-field">
            {getEffectiveMapLabels(effectiveMapList).map((label) => (
              <span className="effective-map-tag" key={label}>{label}</span>
            ))}
          </div>
        )}

        <div className="field">
          <img className="icon" src={remarkIcon} />
          <div id="remark">{remark}</div>
        </div>

        <div className="field">
          <img className="icon" src={editTimeIcon} />
          <div>{modifyTime ?? ''}</div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(PolygonPopup);
